import React from 'react';
import { faPencilAlt, faCheckCircle, faChartBar, faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import EndpointCard from './EndpointCard';
import CodeBlock from './CodeBlock';
import ApiTable from './ApiTable';
import FieldsTable from './FieldsTable';
import { StatusValues } from './Badges';
import { SubHeading } from './Headings';
import type { EndpointSpec } from './types';

/**
 * เรนเดอร์เอกสาร 1 endpoint จากสเปค (ตัว "template" ของหน้า API Docs)
 * ลำดับบล็อกตายตัว: description → intro → requests → response → responseFields → statuses → errors → footer
 */
const EndpointDoc: React.FC<EndpointSpec> = ({
  method, path, description, intro, requests, response, responseFields, statuses, errors, footer,
}) => (
  <EndpointCard method={method} path={path}>
    {description && <p className="text-gray-500 dark:text-slate-400 mb-4 text-sm">{description}</p>}

    {intro}

    {requests?.map((req, idx) => (
      <React.Fragment key={idx}>
        <SubHeading icon={faPencilAlt} spacing={idx > 0 ? 'mt-4' : ''}>{req.heading}</SubHeading>
        {req.note}
        <FieldsTable rows={req.rows} variant={req.variant} />
      </React.Fragment>
    ))}

    {response && (
      <>
        <SubHeading icon={faCheckCircle} iconClassName="text-emerald-500 text-xs">Response ({response.status})</SubHeading>
        <CodeBlock id={response.id} code={response.code} label={response.label || 'response.json'} />
      </>
    )}

    {responseFields && (
      <>
        <SubHeading icon={faChartBar} spacing="mt-5">{responseFields.heading || 'Response Fields'}</SubHeading>
        <FieldsTable rows={responseFields.rows} />
      </>
    )}

    {statuses && (
      <>
        <SubHeading icon={faChartBar} spacing="mt-4">Status Values</SubHeading>
        <StatusValues values={statuses} />
      </>
    )}

    {errors && (
      <>
        <SubHeading icon={faExclamationTriangle} iconClassName="text-rose-400 text-xs" spacing="mt-4">Error Cases</SubHeading>
        <ApiTable
          headers={['HTTP', errors.header]}
          rows={errors.rows.map(row => [
            <span className="font-mono text-xs text-rose-600">{row.http}</span>,
            row.description,
          ])}
        />
      </>
    )}

    {footer}
  </EndpointCard>
);

export default EndpointDoc;
